import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Loader2, MapPin } from 'lucide-react';
import type { SelfServiceFormData } from '../../../types/selfService';
import {
  getZipsInRadius,
  type ZipInRadius,
  type ZipRadiusResult,
} from '../../../services/geographicService';
import type { UseRadiusRecommendationResult } from '../../../services/useRadiusRecommendation';
import { ApiError } from '../../../lib/apiClient';
import USMapWithRadius from './USMapWithRadius';

const MIN_RADIUS_FLOOR = 10;
const MAX_RADIUS = 100;
const DEBOUNCE_MS = 300;

function makeCircleGeoJson(lat: number, lng: number, radiusMiles: number): GeoJSON.Feature {
  const points = 64;
  const coords: [number, number][] = [];
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dLat = (radiusMiles / 69) * Math.cos(angle);
    const dLng = (radiusMiles / 69) * Math.sin(angle) / Math.cos(lat * Math.PI / 180);
    coords.push([lng + dLng, lat + dLat]);
  }
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] }, properties: {} };
}

interface RadiusGeoTargetingProps {
  accentColor: string;
  /**
   * CTV-570 (radius-min v2): live min-radius recommendation from the
   * dedicated endpoint (`useRadiusRecommendation`, lifted at the wizard
   * root). This component is a pure consumer — it doesn't derive the
   * floor itself, it just applies whatever the hook says.
   *
   * Three states matter:
   *   - `!hasResponse` (with or without isCalculating) — spinner; slider
   *     stays hidden until the first backend answer arrives.
   *   - `hasResponse && minMiles = N` — slider mounts with floor=N.
   *   - `hasResponse && minMiles = null` — even 100mi doesn't clear;
   *     slider locks at 100mi, copy points to "run station-wide".
   */
  radiusRecommendation: UseRadiusRecommendationResult;
}

const RadiusGeoTargeting: React.FC<RadiusGeoTargetingProps> = ({ accentColor, radiusRecommendation }) => {
  const { control, watch, setValue } = useFormContext<SelfServiceFormData>();
  const center = watch('geoTargetCenter');
  const radiusMiles = watch('geoTargetRadiusMiles') ?? MIN_RADIUS_FLOOR;

  const [resolvedZips, setResolvedZips] = React.useState<ZipInRadius[]>([]);
  const [summary, setSummary] = React.useState<{ total_zips: number; total_population: number; states_covered: string[] } | null>(null);
  const [fetching, setFetching] = React.useState(false);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  // CTV-570 v2: the recommendation defines the visible floor. Null means
  // "no data" — either the hook hasn't received a response yet (spinner)
  // or the backend explicitly returned null (locked at 100).
  const { minMiles, hasResponse, isCalculating, blockedReason } = radiusRecommendation;
  const lockedAtMax = hasResponse && minMiles === null;
  const floor: number | null = lockedAtMax
    ? MAX_RADIUS
    : (minMiles ?? null);
  // CTV-570 v2 UX fix: the map + slider read the current radius from form
  // state, but the seed effect below writes `floor` into the form async
  // after mount. Until then, `radiusMiles` defaults to MIN_RADIUS_FLOOR
  // (10) — which makes the map render its initial camera zoomed way in.
  // Clamp UP to `floor` for anything that consumes radiusMiles so the
  // map starts at the right zoom.
  const effectiveRadius = floor != null ? Math.max(radiusMiles, floor) : radiusMiles;

  // CTV-678 follow-up: optimistic prefetch, pessimistic render. While the
  // min-radius recommendation is still computing, the ZIP set for the
  // current slider value is fetched in parallel and parked here. Nothing
  // renders from it — the slider and map still gate on `hasResponse`, so
  // the bar always mounts exactly once, in its final position. The
  // prefetch only decides whether the ZIP data is already in hand at that
  // moment. Failures resolve to null and fall back to a live fetch.
  const prefetchRef = React.useRef<{
    key: string;
    radius: number;
    promise: Promise<ZipRadiusResult | null>;
  } | null>(null);
  // Tracks the last center a fetch completed for: the first fetch for a
  // new center fires immediately (there is nothing to debounce), while
  // subsequent radius changes for the same center are slider drags and
  // keep the debounce.
  const lastCenterKeyRef = React.useRef<string | null>(null);

  // Reset resolved-zip state whenever the buyer clears the address
  // upstream (AddressLeadIn writes null to geoTargetCenter).
  React.useEffect(() => {
    if (center) return;
    prefetchRef.current = null;
    lastCenterKeyRef.current = null;
    setResolvedZips([]);
    setSummary(null);
    setFetchError(null);
  }, [center]);

  React.useEffect(() => {
    if (!center || hasResponse) return;
    const key = `${center.lat},${center.lng}`;
    const radius = Math.max(radiusMiles, MIN_RADIUS_FLOOR);
    const existing = prefetchRef.current;
    if (existing && existing.key === key && existing.radius === radius) return;
    prefetchRef.current = {
      key,
      radius,
      promise: getZipsInRadius(center.lat, center.lng, radius).catch(() => null),
    };
  }, [center, hasResponse, radiusMiles]);

  // CTV-570 v2: sync the slider value to the floor when the buyer's
  // current pick is below it. Never push their pick down — floor drops
  // just widen the usable range and leave the thumb where they left it.
  React.useEffect(() => {
    if (!center || floor == null) return;
    if (radiusMiles < floor) {
      setValue('geoTargetRadiusMiles', floor, { shouldDirty: true });
    }
  }, [center, floor, radiusMiles, setValue]);

  // Fetch ZIPs whenever center or the slider value changes.
  // Only fires once the recommendation has arrived so we don't run
  // getZipsInRadius against a stale/unset floor. The first fetch for a
  // center fires immediately (consuming the prefetch when its radius
  // matches the final target); radius changes for the same center are
  // slider drags and stay debounced.
  React.useEffect(() => {
    if (!center || !hasResponse || floor == null) {
      setResolvedZips([]);
      setSummary(null);
      return;
    }
    const target = Math.max(radiusMiles, floor);
    if (target <= 0) {
      setResolvedZips([]);
      setSummary(null);
      return;
    }
    let cancelled = false;
    setFetchError(null);
    const centerKey = `${center.lat},${center.lng}`;
    const run = async () => {
      setFetching(true);
      try {
        const prefetch = prefetchRef.current;
        let result: ZipRadiusResult | null = null;
        if (prefetch && prefetch.key === centerKey && prefetch.radius === target) {
          result = await prefetch.promise;
          prefetchRef.current = null;
        }
        if (!result) {
          result = await getZipsInRadius(center.lat, center.lng, target);
        }
        if (cancelled) return;
        lastCenterKeyRef.current = centerKey;
        setResolvedZips(result.zips);
        setSummary(result.summary);
        setValue('geoTargetZips', result.zips.map((z) => z.zcta), { shouldDirty: true });
      } catch (err) {
        if (cancelled) return;
        console.error('[RadiusGeoTargeting] fetch error', err);
        setFetchError(
          err instanceof ApiError && err.status === 429
            ? 'You are adjusting too quickly. Give it a few seconds and try again.'
            : 'Could not load ZIP codes. Check your connection or try again.',
        );
      } finally {
        if (!cancelled) setFetching(false);
      }
    };
    const delay = lastCenterKeyRef.current === centerKey ? DEBOUNCE_MS : 0;
    const timer = setTimeout(run, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [center, hasResponse, floor, radiusMiles, setValue]);

  const radiusGeoJson = React.useMemo(
    () => (center ? makeCircleGeoJson(center.lat, center.lng, effectiveRadius) : null),
    [center, effectiveRadius],
  );

  const zipBoundariesGeoJson = React.useMemo<GeoJSON.FeatureCollection | null>(() => {
    const withBoundaries = resolvedZips.filter((z) => z.boundary);
    if (withBoundaries.length === 0) return null;
    return {
      type: 'FeatureCollection',
      features: withBoundaries.map((z) => ({
        type: 'Feature' as const,
        geometry: z.boundary as GeoJSON.Geometry,
        properties: { zcta: z.zcta, state: z.state, population: z.population },
      })),
    };
  }, [resolvedZips]);

  // Spinner while we wait for the first backend response OR while a fresh
  // compute is in flight after the buyer changed one of the six triggers.
  // Only shows when we're actually computing — if the hook is gated on a
  // missing prerequisite (dates, budget, etc.), we render a specific
  // "please pick X" message below instead.
  const showSpinner = center && !blockedReason && (!hasResponse || (isCalculating && floor == null));
  const blockedCopy: string | null =
    center && blockedReason
      ? blockedReason === 'no-dates'
        ? 'Add campaign dates above to calculate your optimal coverage.'
        : blockedReason === 'no-budget'
          ? 'Set a campaign budget above to calculate your optimal coverage.'
          : blockedReason === 'no-product'
            ? 'Select a product to calculate your optimal coverage.'
            : null
      : null;

  return (
    <div className="space-y-4">
      {/* Center comes from the shared AddressLeadIn above the tabs now —
          when nothing is set yet, nudge the user upstream. */}
      {!center && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-center">
          <MapPin className="w-5 h-5 mx-auto mb-2 text-slate-400" />
          <p className="text-sm text-slate-600">
            Enter an address or city above to define your radius.
          </p>
        </div>
      )}

      {/* Calculating optimal coverage — real backend compute, not fake. */}
      {showSpinner && (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500 text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: accentColor }} />
          <span>Calculating optimal coverage…</span>
        </div>
      )}

      {/* Missing prerequisite — surface specifically what the buyer needs to fill in. */}
      {blockedCopy && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-center">
          <MapPin className="w-5 h-5 mx-auto mb-2 text-slate-400" />
          <p className="text-sm text-slate-600">{blockedCopy}</p>
        </div>
      )}

      {/* Radius slider — appears once the recommendation has arrived */}
      {center && !showSpinner && floor != null && (
        <Controller
          name="geoTargetRadiusMiles"
          control={control}
          defaultValue={floor}
          render={({ field }) => {
            const value = Math.max(field.value ?? floor, floor);
            const span = MAX_RADIUS - MIN_RADIUS_FLOOR;
            const disabledPct = ((floor - MIN_RADIUS_FLOOR) / span) * 100;
            const fillPct = ((value - MIN_RADIUS_FLOOR) / span) * 100;
            const fullyDisabled = floor >= MAX_RADIUS;
            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-700">Radius</label>
                  <span className="text-[13px] font-semibold tabular-nums" style={{ color: accentColor }}>
                    {value} mi
                  </span>
                </div>
                <input
                  type="range"
                  min={MIN_RADIUS_FLOOR}
                  max={MAX_RADIUS}
                  step={1}
                  value={value}
                  disabled={fullyDisabled}
                  aria-valuemin={floor}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    field.onChange(Math.max(raw, floor));
                  }}
                  className="
                    w-full h-1.5 rounded-full appearance-none
                    focus:outline-none
                    disabled:cursor-not-allowed
                    [&:not(:disabled)]:cursor-pointer
                    [&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent
                    [&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:border-0
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--accent)] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-[0_2px_6px_rgba(15,23,42,0.25)] [&::-webkit-slider-thumb]:-mt-[7px] [&::-webkit-slider-thumb]:transition-transform
                    [&:not(:disabled)]:hover:[&::-webkit-slider-thumb]:scale-110 [&:not(:disabled)]:active:[&::-webkit-slider-thumb]:scale-105
                    [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[var(--accent)] [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-[0_2px_6px_rgba(15,23,42,0.25)]
                    disabled:[&::-webkit-slider-thumb]:bg-slate-400 disabled:[&::-moz-range-thumb]:bg-slate-400
                  "
                  style={{
                    ['--accent' as string]: accentColor,
                    // Track semantics, left to right: [locked below the floor]
                    // [selected fill] [draggable headroom]. The headroom tint
                    // must stay distinguishable from the locked gray for ANY
                    // station accent: at 22% a desaturated navy accent (WISC)
                    // collapsed into the same gray as the locked segment, and
                    // with the thumb parked on the floor (fill width 0) the
                    // whole track read as one inert bar. 45% keeps the
                    // headroom visibly branded and darker than the locked
                    // gray, so "you can widen, you can't narrow" survives the
                    // zero-width-fill case.
                    background: `linear-gradient(to right, rgb(203 213 225) 0%, rgb(203 213 225) ${disabledPct}%, ${accentColor} ${disabledPct}%, ${accentColor} ${fillPct}%, color-mix(in srgb, ${accentColor} 45%, white) ${fillPct}%, color-mix(in srgb, ${accentColor} 45%, white) 100%)`,
                  }}
                />
                <div className="flex justify-between text-[10px] text-slate-400">
                  <span>{MIN_RADIUS_FLOOR} mi</span>
                  <span>{MAX_RADIUS} mi</span>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed pt-1">
                  {fullyDisabled
                    ? 'Even a full 100 mi radius doesn’t have enough inventory for this campaign. Try a longer window, a smaller budget, or run station-wide.'
                    : 'Minimum radius based on campaign details'}
                </p>
              </div>
            );
          }}
        />
      )}

      {/* Map */}
      {center && !showSpinner && floor != null && (
        <div className="relative">
          <USMapWithRadius
            center={center}
            radiusMiles={effectiveRadius}
            radiusGeoJson={radiusGeoJson}
            zipBoundaries={zipBoundariesGeoJson}
          />
          {fetching && (
            <div className="absolute top-2 right-2 bg-white/95 border border-slate-200 rounded-md px-2 py-1 flex items-center gap-1.5 shadow-sm">
              <Loader2 className="w-3 h-3 animate-spin text-slate-500" />
              <span className="text-[11px] text-slate-600">Updating…</span>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      {center && summary && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-slate-600 pt-1">
          <span>
            <strong className="text-slate-900">{summary.total_zips}</strong> ZIP codes
          </span>
          <span>
            <strong className="text-slate-900">{summary.total_population.toLocaleString()}</strong> population
          </span>
          {summary.states_covered.length > 0 && (
            <span>{summary.states_covered.join(', ')}</span>
          )}
        </div>
      )}

      {fetchError && <p className="text-xs text-red-500">{fetchError}</p>}
    </div>
  );
};

export default RadiusGeoTargeting;
